import { Injectable } from '@angular/core';

import { Capacitor } from '@capacitor/core';

import { saveAs } from 'file-saver';

import { FileWriteData, FileWriteOutput } from './file-write.types';
import { FileOpener } from '@capacitor-community/file-opener';
import { Directory, Filesystem } from '@capacitor/filesystem';

@Injectable({
  providedIn: 'root',
})
export class FileWriteService {
  async writeFile(data: FileWriteData): Promise<FileWriteOutput> {
    return new Promise(async (res, rej) => {
      try {
        if (Capacitor.getPlatform() === 'web') {
          await this.writeWeb(data);

          res({
            uri: null,
          });
        } else {
          const dataMobile = await this.writeMobile(data);

          res({
            uri: dataMobile.uri,
          });
        }
      } catch (err) {
        rej(JSON.stringify(err));
      }
    });
  }

  private async writeMobile(data: FileWriteData): Promise<{ uri: string }> {
    await this.writeAndOpenFile(data.blob, data.path, data.mimeType);
    return { uri: null };
  }

  private async writeAndOpenFile(
    data: Blob,
    fileName: string,
    mimeType?: string
  ) {
    const reader = new FileReader();
    reader.readAsDataURL(data);
    reader.onloadend = async function () {
      const base64data = reader.result;
      try {
        const result = await Filesystem.writeFile({
          path: fileName,
          data: <string>base64data,
          directory: Directory.Data,
          recursive: true,
        });

        // Determine content type: prefer blob's actual type, then explicit mimeType, then file extension
        const contentType =
          data.type ||
          mimeType ||
          FileWriteService.getMimeTypeFromExtension(fileName);

        await FileOpener.open({
          filePath: result.uri,
          contentType,
        });
      } catch (e) {
        console.error('Unable to write file', e);
      }
    };
  }

  private async writeWeb(data: FileWriteData) {
    saveAs(data.blob, data.path);
  }

  private convertBlobToBase64(blob: Blob) {
    return new Promise((res, rej) => {
      const reader = new FileReader();

      reader.onerror = rej;
      reader.onload = () => {
        res(reader.result);
      };

      reader.readAsDataURL(blob);
    });
  }

  private static getMimeTypeFromExtension(fileName: string): string {
    const extension = fileName.split('.').pop()?.toLowerCase();
    const mimeTypes: { [key: string]: string } = {
      pdf: 'application/pdf',
      png: 'image/png',
      jpg: 'image/jpeg',
      jpeg: 'image/jpeg',
      gif: 'image/gif',
      svg: 'image/svg+xml',
      webp: 'image/webp',
      txt: 'text/plain',
      html: 'text/html',
      css: 'text/css',
      js: 'application/javascript',
      json: 'application/json',
      xml: 'application/xml',
      zip: 'application/zip',
      doc: 'application/msword',
      docx:
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      xls: 'application/vnd.ms-excel',
      xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      ppt: 'application/vnd.ms-powerpoint',
      pptx:
        'application/vnd.openxmlformats-officedocument.presentationml.presentation',
      mp4: 'video/mp4',
      mp3: 'audio/mpeg',
      wav: 'audio/wav',
    };
    return mimeTypes[extension] || 'application/octet-stream';
  }
}
